import { gql } from '@apollo/client';

import {
  MARKETING_CAMPAIGN_ALIAS_FIELDS,
  MARKETING_CHANNEL_RULE_FIELDS,
  MARKETING_COURSE_ALIAS_FIELDS,
  MARKETING_METRICS_FIELDS,
  MARKETING_OFFER_RULE_FIELDS,
  MARKETING_PIPELINE_FIELDS,
  MARKETING_SEARCH_ROW_FIELDS,
  MARKETING_SIGNAL_FIELDS,
  MARKETING_SOURCE_STATUS_FIELDS,
  MARKETING_TARGET_FIELDS,
} from '../fragments/marketing';

/**
 * ТЗ 0088 (этап 1) — чтение раздела «Маркетинг». Доступ — Admin и Marketing
 * (`MARKETING_READ_ROLES` бэкенда); без права доменный хук запрос не шлёт.
 *
 * Имена операций намеренно НЕ совпадают с полями бэкенда
 * (`marketingOverview` → `MarketingOverviewReport` и т. д.): совпадение имени
 * операции и поля схлопывает тип результата в кодгене (скилл graphql-codegen).
 *
 * Ошибки окна и параметров (`window_too_long`, `day_granularity_window`,
 * `limit_too_large`, `bad_order`…) приходят GraphQL-ошибкой с
 * `extensions.code` — перевод по коду делает доменный хук.
 */

/**
 * «Пульс»: плитки, прошлое окно, покрытие, ряд, доли каналов, источники и
 * (этап 2) план-факт месяцев окна.
 */
export const MarketingOverviewReportQuery = gql`
  query MarketingOverviewReport(
    $filter: MarketingFilter!
    $granularity: MarketingGranularity
  ) {
    marketingOverview(filter: $filter, granularity: $granularity) {
      current {
        ...MarketingMetricsFields
      }
      previous {
        ...MarketingMetricsFields
      }
      coverage {
        spendComplete
        missingSpendDays
        provisionalFrom
      }
      series {
        period
        metrics {
          ...MarketingMetricsFields
        }
      }
      channels {
        channel
        metrics {
          ...MarketingMetricsFields
        }
      }
      sources {
        ...MarketingSourceStatusFields
      }
      dataVersion
      planFact {
        metric
        target
        fact
        pace
      }
    }
  }
  ${MARKETING_METRICS_FIELDS}
  ${MARKETING_SOURCE_STATUS_FIELDS}
`;

/**
 * Версия данных без показателей — сторож пересчёта после правки справочника.
 * Окно — один день: ответ дешёвый и кешируется сервером до новой версии.
 */
export const MarketingDataVersionProbeQuery = gql`
  query MarketingDataVersionProbe($filter: MarketingFilter!) {
    marketingOverview(filter: $filter) {
      dataVersion
    }
  }
`;

/**
 * Таблица разреза «Каналов» и карточки «Курсов». Фильтр авторитетен (спуск
 * сужает его), `parentKey` — только в цепочке канал → кампания → группа.
 * `series` приходит лишь при `withSeries: true` и не больше чем у 20 строк.
 * `crumbs` — имена кампаний и групп из фильтра спуска: крошки свежей ссылки
 * подписываются ими, а не id.
 */
export const MarketingBreakdownReportQuery = gql`
  query MarketingBreakdownReport(
    $filter: MarketingFilter!
    $dimension: MarketingDimension!
    $parentKey: String
    $withSeries: Boolean
    $granularity: MarketingGranularity
    $limit: Int
    $offset: Int
    $orderBy: String
  ) {
    marketingBreakdown(
      filter: $filter
      dimension: $dimension
      parentKey: $parentKey
      withSeries: $withSeries
      granularity: $granularity
      limit: $limit
      offset: $offset
      orderBy: $orderBy
    ) {
      dimension
      totalCount
      crumbs {
        id
        name
      }
      total {
        ...MarketingMetricsFields
      }
      rows {
        key
        label
        platform
        status
        format
        thumbnailUrl
        salesCoverage
        metrics {
          ...MarketingMetricsFields
        }
        series {
          period
          metrics {
            ...MarketingMetricsFields
          }
        }
      }
    }
  }
  ${MARKETING_METRICS_FIELDS}
`;

/**
 * Матрица двух измерений («Каналы»: канал × курс) — плоский список НЕПУСТЫХ
 * ячеек, отсортированный по `rowKey`, `colKey`. Ключ канала — код базы
 * (`meta`), курс без семейства — `none` (подпись пустая). При модели
 * `PARTICIPATION` ячейки — «участие» и в итог не складываются; итога у
 * матрицы нет.
 */
export const MarketingMatrixReportQuery = gql`
  query MarketingMatrixReport(
    $filter: MarketingFilter!
    $rows: MarketingDimension
    $cols: MarketingDimension
  ) {
    marketingMatrix(filter: $filter, rows: $rows, cols: $cols) {
      rowKey
      rowLabel
      colKey
      colLabel
      metrics {
        ...MarketingMetricsFields
      }
    }
  }
  ${MARKETING_METRICS_FIELDS}
`;

/** Ряд одной строки разреза (фильтр сужен до неё). */
export const MarketingSeriesReportQuery = gql`
  query MarketingSeriesReport(
    $filter: MarketingFilter!
    $granularity: MarketingGranularity
  ) {
    marketingSeries(filter: $filter, granularity: $granularity) {
      period
      metrics {
        ...MarketingMetricsFields
      }
    }
  }
  ${MARKETING_METRICS_FIELDS}
`;

/**
 * Продажи окна с касаниями («Качество данных»). Email у роли Marketing
 * приходит замаскированным — экран показывает как пришло.
 */
export const MarketingSalesReportQuery = gql`
  query MarketingSalesReport(
    $filter: MarketingFilter!
    $onlySales: Boolean
    $coverage: String
    $limit: Int
    $offset: Int
  ) {
    marketingSales(
      filter: $filter
      onlySales: $onlySales
      coverage: $coverage
      limit: $limit
      offset: $offset
    ) {
      totalCount
      rows {
        id
        source
        sourceId
        day
        kind
        isSale
        saleReason
        familySlug
        familyName
        pricePln
        cashPln
        emailMasked
        leadKommoId
        coverage
        lastChannel
        lastCampaign
        lastAd
        firstChannel
        leadToSaleDays
        touches {
          at
          pipelineKind
          channel
          campaign
          ad
          matchRule
          isFirst
          isLast
        }
      }
    }
  }
`;

/**
 * «Качество данных»: покрытие, потеря меток, несопоставленное, сверка.
 * Этап 2: касания Meta всех воронок (лиды площадки — события пикселя любой
 * формы сайта, сравнивать надо с ними), входы в New leads по источнику лида
 * Kommo и признаки несопоставленной кампании (`familyMissing` —
 * «Создать правило», `campaignMissing` — «Сопоставить с кампанией»).
 */
export const MarketingDataQualityReportQuery = gql`
  query MarketingDataQualityReport($filter: MarketingFilter!) {
    marketingDataQuality(filter: $filter) {
      salesCoverage {
        level
        count
      }
      metaPlatformLeads
      metaCrmLeads
      metaTouchesAll
      crmLeadsBySource {
        value
        count
      }
      crmLeadsWithoutCourse
      crmLeadsTotal
      salesUnknownCourseRepeats
      unmappedSources {
        field
        value
        count
        platform
        familyMissing
        campaignMissing
      }
      unmappedCampaigns {
        field
        value
        count
        platform
        familyMissing
        campaignMissing
      }
      accountReconciliation {
        platform
        day
        campaignSum
        accountTotal
        diffPct
      }
      sources {
        ...MarketingSourceStatusFields
      }
    }
  }
  ${MARKETING_SOURCE_STATUS_FIELDS}
`;

/** Статус источников — «Настройки» и опрос после «Обновить сейчас». */
export const MarketingSourcesStatusQuery = gql`
  query MarketingSourcesStatus {
    marketingSources {
      ...MarketingSourceStatusFields
    }
  }
  ${MARKETING_SOURCE_STATUS_FIELDS}
`;

/**
 * Справочники «Настроек» плюс варианты фильтров: юрлица (роль Marketing иначе
 * справочника юрлиц не видит) и курсы ERP для алиаса `erp_course`. Этап 2 —
 * ручные сопоставления меток CRM с кампаниями кабинета. Этап 3 — регулярка
 * бренда поиска, ключевые события GA4 (колонки «Посадочных») и хосты своего
 * сайта (страница другого хоста — не «наша»). Этап 4 — правило «сигналы →
 * задачи» и сотрудники, которых можно назначить (только те, кто видит задачи
 * ERP; только id и имя).
 */
export const MarketingTaxonomyReportQuery = gql`
  query MarketingTaxonomyReport {
    marketingTaxonomy {
      channelRules {
        ...MarketingChannelRuleFields
      }
      families {
        id
        slug
        name
        isSold
        sort
        aliases {
          ...MarketingCourseAliasFields
        }
      }
      offerRules {
        ...MarketingOfferRuleFields
      }
      pipelines {
        ...MarketingPipelineFields
      }
      legalEntities {
        id
        name
      }
      courses {
        id
        name
      }
      campaignAliases {
        ...MarketingCampaignAliasFields
      }
      brandRegex
      ga4KeyEvents
      siteHosts
      signalRouting {
        kinds
        minSeverity
        assigneeIds
      }
      staffOptions {
        id
        name
      }
    }
  }
  ${MARKETING_CHANNEL_RULE_FIELDS}
  ${MARKETING_COURSE_ALIAS_FIELDS}
  ${MARKETING_OFFER_RULE_FIELDS}
  ${MARKETING_PIPELINE_FIELDS}
  ${MARKETING_CAMPAIGN_ALIAS_FIELDS}
`;

/* ── Этап 2 ──────────────────────────────────────────────────────────── */

/**
 * «Креативы»: объявления окна с превью, форматом, видео-метриками и
 * выгоранием (окна выгорания — от СЕГОДНЯ, не от фильтра). `series` — только
 * при `withSeries: true` и не больше чем у 50 строк страницы; `formats` —
 * «формат × семейство × оффер» по всем строкам выдачи, до страницы.
 *
 * `adIds` — ровно эти объявления, мимо измерений фильтра, формата и
 * «только выгорающих» (окно, модель и юрлицо считают цифры): так грузится
 * выбранное объявление — ссылкой сигнала или кликом строки — вместе с его
 * рядом, не перечитывая страницу. `[]` — пусто, `null` — без отбора.
 */
export const MarketingCreativesReportQuery = gql`
  query MarketingCreativesReport(
    $filter: MarketingFilter!
    $fatigueOnly: Boolean
    $format: String
    $withSeries: Boolean
    $orderBy: String
    $limit: Int
    $offset: Int
    $adIds: [ID!]
  ) {
    marketingCreatives(
      filter: $filter
      fatigueOnly: $fatigueOnly
      format: $format
      withSeries: $withSeries
      orderBy: $orderBy
      limit: $limit
      offset: $offset
      adIds: $adIds
    ) {
      totalCount
      rows {
        id
        platform
        externalId
        name
        level
        crmScope
        campaignId
        campaignName
        adsetName
        format
        thumbnailUrl
        title
        status
        firstDay
        ageDays
        familySlug
        offer
        metrics {
          ...MarketingMetricsFields
        }
        frequency
        reach28d
        hookRate
        holdRate
        cplPlatformPln
        conversionsPlatform
        fatigue {
          flagged
          reasons
          ctr7d
          ctrBase
          ctrStart
          ctrPrev
          frequency28d
          cpm7d
          cpmPrev
          cpmChangePct
          cpl7d
          cplMedian
          cplRatio
          hookRate
          hookPrev
          holdRate
          holdPrev
        }
        series {
          period
          metrics {
            ...MarketingMetricsFields
          }
        }
      }
      formats {
        format
        familySlug
        offer
        ads
        hookRate
        metrics {
          ...MarketingMetricsFields
        }
      }
    }
  }
  ${MARKETING_METRICS_FIELDS}
`;

/** Когорты недель входа в New leads: продажи за 30 / 60 / 90 дней. */
export const MarketingCohortsReportQuery = gql`
  query MarketingCohortsReport($filter: MarketingFilter!) {
    marketingCohorts(filter: $filter) {
      cohort
      leads
      sales30
      sales60
      sales90
      revenue90Pln
      mature30
      mature60
      mature90
    }
  }
`;

/**
 * «Путь клиента»: косвенные воронки → New leads → покупка, покупатели внутри
 * New leads по числу косвенных касаний (модель `within_new_leads`), лаг
 * лид → продажа, скорость первой реакции.
 *
 * Лаг — `lagSales` (ПРОДАЖИ окна по корзинам дней): старое `lag` отдавало
 * продажи в поле `people` и сервером объявлено устаревшим.
 *
 * `byDevice` (29.09) — устройство заявки: CRM-лиды окна по устройству входа,
 * продажи окна по устройству лида-конверсии; всегда mobile, desktop, tablet,
 * unknown, сумма строк = итогу окна.
 */
export const MarketingJourneyReportQuery = gql`
  query MarketingJourneyReport($filter: MarketingFilter!) {
    marketingJourney(filter: $filter) {
      indirect {
        pipelineKind
        people
        reachedMain
        buyers
      }
      withinMain {
        key
        people
        buyers
      }
      lagSales {
        key
        sales
      }
      speedToLead {
        key
        people
        buyers
      }
      historyFrom
      byDevice {
        device
        crmLeads
        crmLeadsRepeat
        sales
        revenuePln
        conversion
      }
    }
  }
`;

/** План месяца: строки «канал × семейство × показатель». */
export const MarketingTargetsListQuery = gql`
  query MarketingTargetsList($month: Date!) {
    marketingTargets(month: $month) {
      ...MarketingTargetFields
    }
  }
  ${MARKETING_TARGET_FIELDS}
`;

/**
 * Лента сигналов (по умолчанию сервера — открытые и принятые). Читается без
 * кеша сервера: решение «принято / скрыть» видно сразу.
 */
export const MarketingSignalsFeedQuery = gql`
  query MarketingSignalsFeed(
    $status: [String!]
    $kinds: [String!]
    $limit: Int
  ) {
    marketingSignals(status: $status, kinds: $kinds, limit: $limit) {
      ...MarketingSignalFields
    }
  }
  ${MARKETING_SIGNAL_FIELDS}
`;

/**
 * Кампании площадки для «Сопоставить с кампанией»: имя содержит `search`,
 * первые 50 по расходу за 30 дней.
 */
export const MarketingCampaignChoicesQuery = gql`
  query MarketingCampaignChoices($platform: String!, $search: String) {
    marketingCampaignOptions(platform: $platform, search: $search) {
      id
      name
    }
  }
`;

/* ── Этап 3: поиск, сайт, спрос ──────────────────────────────────────── */

/**
 * Отчёт поисковика: ровно один `engine` — Google (GSC) или Bing, они не
 * складываются никогда. `total` — полный итог окна (единственная полная
 * цифра кликов); строки — неполный срез, `namedClicksShare` — какую долю
 * кликов итога они раскрывают (у Bing — NULL). Зоны позиций считаются по
 * строкам запросов с фильтрами бренда и семейства. У Bing строки — его
 * снимки, `snapshotDate` — когда их прочли. `includesSubdomains` — итог
 * по всему домену (ресурс GSC доменный), `otherHosts` — чужие хосты окна
 * (lms., webinars.…), которые в него вошли; у Bing — `false` и пусто.
 */
export const MarketingSearchEngineReportQuery = gql`
  query MarketingSearchEngineReport(
    $filter: MarketingFilter!
    $engine: String
    $dimension: String
    $brand: Boolean
    $zone: String
    $familySlug: String
    $search: String
    $orderBy: String
    $limit: Int
    $offset: Int
  ) {
    marketingSearch(
      filter: $filter
      engine: $engine
      dimension: $dimension
      brand: $brand
      zone: $zone
      familySlug: $familySlug
      search: $search
      orderBy: $orderBy
      limit: $limit
      offset: $offset
    ) {
      engine
      total {
        day
        clicks
        impressions
        position
      }
      series {
        day
        clicks
        impressions
        position
      }
      namedClicksShare
      zones {
        zone
        queries
        impressions
        clicks
      }
      rows {
        ...MarketingSearchRowFields
      }
      totalCount
      provisionalFrom
      snapshotDate
      includesSubdomains
      otherHosts
    }
  }
  ${MARKETING_SEARCH_ROW_FIELDS}
`;

/**
 * Итог поисковика за ПРОШЛОЕ окно — для изменения плиток. Дни ряда нужны,
 * чтобы понять, собрано ли окно целиком: без этого «кликов стало больше»
 * могло бы значить «в прошлом окне не хватало дней».
 */
export const MarketingSearchTotalsProbeQuery = gql`
  query MarketingSearchTotalsProbe($filter: MarketingFilter!, $engine: String) {
    marketingSearch(filter: $filter, engine: $engine, limit: 1) {
      engine
      total {
        day
        clicks
        impressions
        position
      }
      series {
        day
      }
    }
  }
`;

/**
 * Трафик сайта GA4 по группе каналов (+ источник / кампания). `users` —
 * СУММА дневных пользователей: между днями не складывается в людей.
 */
export const MarketingSiteTrafficReportQuery = gql`
  query MarketingSiteTrafficReport(
    $filter: MarketingFilter!
    $groupBy: String
  ) {
    marketingTraffic(filter: $filter, groupBy: $groupBy) {
      channelGroup
      source
      medium
      campaign
      sessions
      users
      engaged
      keyEvents
    }
  }
`;

/**
 * Посадочные GA4: сессии, вовлечённые, ключевые события (JSONString
 * `{событие: число}` по списку из настроек) и клики / показы GSC той же
 * страницы — рядом, НЕ складываясь. GSC NULL — не собран; 0 — собран, а
 * строк страницы нет.
 */
export const MarketingLandingPagesReportQuery = gql`
  query MarketingLandingPagesReport(
    $filter: MarketingFilter!
    $familySlug: String
    $limit: Int
    $offset: Int
  ) {
    marketingLandings(
      filter: $filter
      familySlug: $familySlug
      limit: $limit
      offset: $offset
    ) {
      page
      familySlug
      sessions
      engaged
      keyEvents
      gscClicks
      gscImpressions
    }
  }
`;

/** Переходы из AI-ассистентов: строка на «ассистент × посадочная». */
export const MarketingAiAssistantsReportQuery = gql`
  query MarketingAiAssistantsReport($filter: MarketingFilter!) {
    marketingAiReferrals(filter: $filter) {
      assistant
      landingPage
      sessions
      engaged
    }
  }
`;

/**
 * Спрос по семействам: небрендовые показы и клики GSC с прошлым окном,
 * топ-запросы, CRM-лиды с этим курсом и продажи. Семейства `isSold=false` —
 * «спрос, который мы не обслуживаем».
 */
export const MarketingSearchDemandReportQuery = gql`
  query MarketingSearchDemandReport($filter: MarketingFilter!) {
    marketingDemand(filter: $filter) {
      familySlug
      familyName
      isSold
      impressions
      prevImpressions
      clicks
      topQueries {
        ...MarketingSearchRowFields
      }
      crmLeads
      sales
    }
  }
  ${MARKETING_SEARCH_ROW_FIELDS}
`;

/**
 * Поисковые запросы Google Ads (кампании Search): `coverageShare` — какую
 * долю расхода поисковых кампаний они раскрывают (Google прячет редкие).
 * `conversions` — цифры Google, не лиды.
 */
export const MarketingAdsSearchTermsReportQuery = gql`
  query MarketingAdsSearchTermsReport(
    $filter: MarketingFilter!
    $familySlug: String
    $orderBy: String
    $limit: Int
    $offset: Int
  ) {
    marketingSearchTerms(
      filter: $filter
      familySlug: $familySlug
      orderBy: $orderBy
      limit: $limit
      offset: $offset
    ) {
      totalCount
      coverageShare
      rows {
        term
        campaign
        adGroup
        impressions
        clicks
        spendPln
        conversions
        familySlug
      }
    }
  }
`;

/* ── Этап 4: прогноз, недельный снимок, AI-сводка, письма ────────────── */

/**
 * Прогноз продаж месяца по семейству курса. Без `month` — текущий месяц по
 * Варшаве; прошедший месяц — факт (P10 = P50 = P90 = продажи месяца);
 * будущий — отказ `bad_month`. `smallBase` — меньше 30 лидов за 180 дней:
 * P10 / P90 не считаются (null), остаётся одна медиана. Этап лида `unknown`
 * — лид без этапа Kommo; `basis` — `stage` (вероятность этапа) или
 * `overall` (у этапа мало наблюдений — общая конверсия курса). Группы —
 * со стартом в ближайшие 60 дней; `seats` 0 — набор закрыт, null — мест
 * не задано.
 */
export const MarketingSalesForecastReportQuery = gql`
  query MarketingSalesForecastReport($month: Date, $familySlug: String) {
    marketingForecast(month: $month, familySlug: $familySlug) {
      familySlug
      familyName
      month
      salesMtd
      p10
      p50
      p90
      target
      smallBase
      openLeads {
        stage
        leads
        probability
        basis
        observations
      }
      expectedNewLeads
      leadToSale
      daysLeft
      pace28d
      expectedFromOpenLeads
      expectedFromNewLeads
      groups {
        groupId
        name
        startDate
        seats
        enrolled
        expectedBeforeStart
        fillP50
        risk
      }
    }
  }
`;

/**
 * Недельные снимки (замороженный JSON завершённой ISO-недели): только
 * `summary` — сжатые факты для полосы плиток «AI-разбора». Полный `payload`
 * экрану не нужен и сюда не выбирается.
 */
export const MarketingWeeklySnapshotsListQuery = gql`
  query MarketingWeeklySnapshotsList($limit: Int, $isoWeek: String) {
    marketingSnapshots(limit: $limit, isoWeek: $isoWeek) {
      id
      isoWeek
      dateFrom
      dateTo
      frozenAt
      dataVersion
      rebuiltCount
      summary
    }
  }
`;

/**
 * История AI-сводок (новые сверху) — без текста: список недель и статусов
 * не должен тянуть десяток длинных текстов. Статус сводки меняется без новой
 * версии данных — сервер читает его без кеша, экран опрашивает, пока есть
 * `queued` / `running`.
 */
export const MarketingDigestHistoryListQuery = gql`
  query MarketingDigestHistoryList($limit: Int) {
    marketingDigests(limit: $limit) {
      id
      isoWeek
      status
      model
      factsChecked
      rejectedNumbers
      attempts
      createdAt
      createdBy
      startedAt
      finishedAt
      error
    }
  }
`;

/**
 * Сводки ОДНОЙ недели с текстом (`text` есть только у `published`): экран
 * показывает последнюю опубликованную, даже если поверх неё уже готовится
 * или отклонена пересборка.
 */
export const MarketingDigestWeekTextQuery = gql`
  query MarketingDigestWeekText($isoWeek: String!, $limit: Int) {
    marketingDigests(isoWeek: $isoWeek, limit: $limit) {
      id
      isoWeek
      status
      text
      model
      factsChecked
      rejectedNumbers
      attempts
      createdAt
      createdBy
      startedAt
      finishedAt
      error
      inputTokens
      outputTokens
    }
  }
`;

/**
 * Письма SendGrid за окно: рассылки (single sends) со строкой в окне, ряд
 * по дням (`clicks` — уникальные клики, `impressions` — доставлено,
 * `position` всегда null), итоги рассылок, лиды и продажи с меткой письма
 * (совпадение метки, НЕ причинность). `account` и `categories` — весь
 * аккаунт, включая транзакционные письма ERP и N8N: с рассылками не
 * складываются.
 */
export const MarketingSendgridEmailReportQuery = gql`
  query MarketingSendgridEmailReport($filter: MarketingFilter!) {
    marketingEmail(filter: $filter) {
      sends {
        id
        name
        sentAt
        delivered
        uniqueOpens
        uniqueClicks
        unsubscribes
        bounces
        emailLeads
        emailSales
        categories
      }
      series {
        day
        clicks
        impressions
        position
      }
      delivered
      uniqueOpens
      uniqueClicks
      emailLeads
      emailSales
      account {
        delivered
        uniqueOpens
        uniqueClicks
        requests
      }
      categories {
        name
        delivered
        uniqueOpens
        uniqueClicks
      }
    }
  }
`;
